import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Tooltip,
  Popover,
  Button,
  Typography,
  Paper,
  Divider,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Chip
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  Link,
  Code,
  EmojiEmotions,
  AttachFile,
  Mic,
  Send,
  EditNote,
  QuestionAnswer
} from '@mui/icons-material';
import { VoiceMessageIcon } from '../icons/VoiceMessageIcon';
import { SpeechToTextIcon } from '../icons/SpeechToTextIcon';
import api from '../../services/api';

interface CannedResponse {
  id: number;
  short_code: string;
  content: string;
  is_private: boolean;
  usage_count: number;
}

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  onAttachFile?: () => void;
  onStartRecording?: () => void;
  onStartSpeechToText?: () => void;
  onOpenSignatureDialog?: () => void;
  placeholder?: string;
  disabled?: boolean;
  showSignatureButton?: boolean;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  onSend,
  onAttachFile,
  onStartRecording,
  onStartSpeechToText,
  onOpenSignatureDialog,
  placeholder = "Type your message...",
  disabled = false,
  showSignatureButton = true
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [showFormatting, setShowFormatting] = useState(false);
  const textFieldRef = useRef<HTMLTextAreaElement>(null);

  // Canned responses state
  const [cannedResponses, setCannedResponses] = useState<CannedResponse[]>([]);
  const [showCannedResponses, setShowCannedResponses] = useState(false);
  const [cannedResponseAnchorEl, setCannedResponseAnchorEl] = useState<HTMLElement | null>(null);
  const [cannedSearchQuery, setCannedSearchQuery] = useState('');
  const [slashPosition, setSlashPosition] = useState<number>(-1);

  const emojiCategories: Record<string, string[]> = {
    'Smileys': ['😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚', '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🥸', '🤩', '🥳', '😏', '😒', '😞', '😔', '😟', '😕', '🙁', '☹️', '😣', '😖', '😫', '😩', '🥺', '😢', '😭', '😤', '😠', '😡', '🤬', '🤯', '😳', '🥵', '🥶', '😱', '😨', '😰', '😥', '😓', '🤗', '🤔', '🤭', '🤫', '🤥', '😶', '😐', '😑', '😬', '🙄', '😯', '😦', '😧', '😮', '😲', '🥱', '😴', '🤤', '😪', '😵', '🤐', '🥴', '🤢', '🤮', '🤧', '😷', '🤒', '🤕'],
    'Gestures': ['👋', '🤚', '🖐', '✋', '🖖', '👌', '🤌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '👍', '👎', '👊', '✊', '🤛', '🤜', '👏', '🙌', '👐', '🤲', '🤝', '🙏', '✍️', '💪', '🦾', '🦵', '🦿', '🦶'],
    'Hearts': ['❤️', '🧡', '💛', '💚', '💙', '💜', '🤎', '🖤', '🤍', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟'],
    'Celebration': ['🎉', '🎊', '🎈', '🎂', '🎁', '🎀', '🎗️', '🎟️', '🎫', '🎪', '🎭', '🎨', '🎬', '🎤', '🎧', '🎼', '🎵', '🎶', '🎯', '🎲', '🎳', '🎮', '🎰', '🧩'],
    'Objects': ['💯', '🔥', '✨', '🌟', '💫', '⭐', '🌠', '💥', '💢', '💨', '💤', '🕳️', '💦', '💧', '💣', '💬', '👁️‍🗨️', '💭', '💤', '📢', '📣', '📯', '🔔', '🔕', '📞', '📱', '💻', '⌨️', '🖥️', '🖨️', '🖱️', '💾', '💿', '📀', '💽'],
    'Activities': ['⚽', '🏀', '🏈', '⚾', '🥎', '🎾', '🏐', '🏉', '🥏', '🎱', '🪀', '🏓', '🏸', '🏒', '🏑', '🥍', '🏏', '🪃', '🥅', '⛳', '🪁', '🏹', '🎣', '🤿', '🥊', '🥋', '🎽', '🛹', '🛷', '⛸️', '🥌', '🛼', '🛴', '🚲'],
    'Travel': ['🚗', '🚕', '🚙', '🚌', '🚎', '🏎️', '🚓', '🚑', '🚒', '🚐', '🚚', '🚛', '🚜', '🏍️', '🛵', '🚲', '🛴', '🛹', '🛼', '🚁', '🛸', '✈️', '🛩️', '🛫', '🛬', '🪂', '⛵', '🚤', '🛥️', '🛳️', '⛴️', '🚢', '⚓', '⛽', '🚧'],
    'Nature': ['🌱', '🌿', '☘️', '🍀', '🎋', '🎍', '🌾', '🌵', '🌲', '🌳', '🌴', '🌹', '🥀', '🌺', '🌻', '🌼', '🌷', '⚘️', '🌸', '💐', '🍄', '🌰', '🎃', '🐚', '🌎', '🌍', '🌏', '💫', '⭐', '🌟', '✨', '⚡', '☄️', '💥', '🔥', '🌪️', '🌈', '☀️', '🌤️', '⛅', '🌦️', '🌧️', '⛈️', '🌩️', '🌨️', '❄️', '☃️', '⛄', '🌊', '💧', '💦'],
    'Food': ['🍎', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓', '🫐', '🍈', '🍒', '🍑', '🥭', '🍍', '🥥', '🥝', '🍅', '🍆', '🥑', '🥦', '🥬', '🥒', '🌶️', '🫑', '🌽', '🥕', '🧄', '🧅', '🥔', '🍠', '🥐', '🥖', '🍞', '🥨', '🥯', '🥞', '🧇', '🧀', '🍖', '🍗', '🥩', '🥓', '🍔', '🍟', '🍕', '🌭', '🥪', '🌮', '🌯', '🫔', '🥙', '🧆', '🥚', '🍳', '🥘', '🍲', '🫕', '🥣', '🥗', '🍿', '🧈', '🧂', '🥫', '🍱', '🍘', '🍙', '🍚', '🍛', '🍜', '🍝', '🍠', '🍢', '🍣', '🍤', '🍥', '🥮', '🍡', '🥟', '🥠', '🥡', '🦪', '🍦', '🍧', '🍨', '🍩', '🍪', '🎂', '🍰', '🧁', '🥧', '🍫', '🍬', '🍭'],
    'Animals': ['🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐻‍❄️', '🐨', '🐯', '🦁', '🐮', '🐷', '🐸', '🐵', '🙈', '🙉', '🙊', '🐒', '🐔', '🐧', '🐦', '🐤', '🐣', '🐥', '🦆', '🦅', '🦉', '🦇', '🐺', '🐗', '🐴', '🦄', '🐝', '🐛', '🦋', '🐌', '🐞', '🐜', '🦟', '🦗', '🕷️', '🕸️', '🦂', '🐢', '🐍', '🦎', '🦖', '🦕', '🐙', '🦑', '🦐', '🦞', '🦀', '🐡', '🐠', '🐟', '🐕', '🐩', '🦮', '🐕‍🦺', '🐈', '🐈‍⬛', '🦓', '🦒', '🦔'],
    'Flags': ['🏁', '🚩', '🏴', '🏳️', '🏳️‍🌈', '🏳️‍⚧️', '🏴‍☠️', '🇺🇸', '🇬🇧', '🇨🇦', '🇦🇺', '🇮🇳', '🇨🇳', '🇯🇵', '🇰🇷', '🇩🇪', '🇫🇷', '🇪🇸', '🇮🇹', '🇧🇷', '🇲🇽', '🇷🇺', '🇪🇺', '🇸🇦', '🇹🇷', '🇳🇬', '🇿🇦'],
  };

  const [selectedEmojiCategory, setSelectedEmojiCategory] = useState<keyof typeof emojiCategories>('Smileys');

  // Fetch canned responses on mount
  useEffect(() => {
    const fetchCannedResponses = async () => {
      try {
        const response = await api.get('/canned-responses/?include_private=true');
        setCannedResponses(response.data);
      } catch (error) {
        console.error('Failed to fetch canned responses:', error);
      }
    };
    fetchCannedResponses();
  }, []);

  // Detect "/" character and show canned responses
  useEffect(() => {
    if (!textFieldRef.current) return;

    const textarea = textFieldRef.current;
    const cursorPosition = textarea.selectionStart;
    const textBeforeCursor = value.substring(0, cursorPosition);

    // Check if "/" is at the start or after a space/newline
    const slashMatch = textBeforeCursor.match(/(?:^|\s|\n)(\/\w*)$/);

    if (slashMatch) {
      const slashIndex = textBeforeCursor.lastIndexOf(slashMatch[1]);
      const searchTerm = slashMatch[1].substring(1); // Remove the "/"

      setSlashPosition(slashIndex);
      setCannedSearchQuery(searchTerm);
      setShowCannedResponses(true);
      setCannedResponseAnchorEl(textarea);
    } else {
      setShowCannedResponses(false);
      setSlashPosition(-1);
      setCannedSearchQuery('');
    }
  }, [value]);

  const handleEmojiClick = (emoji: string) => {
    const newValue = value + emoji;
    onChange(newValue);
    setAnchorEl(null);
  };

  const handleCannedResponseSelect = async (response: CannedResponse) => {
    if (slashPosition === -1 || !textFieldRef.current) return;

    // Replace the "/search" text with the canned response content
    const beforeSlash = value.substring(0, slashPosition);
    const afterSlash = value.substring(textFieldRef.current.selectionStart);
    const newValue = beforeSlash + response.content + afterSlash;

    onChange(newValue);
    setShowCannedResponses(false);
    setSlashPosition(-1);
    setCannedSearchQuery('');

    // Track usage
    try {
      await api.post(`/canned-responses/${response.id}/use`);
    } catch (error) {
      console.error('Failed to track canned response usage:', error);
    }

    // Focus back on textarea
    setTimeout(() => {
      if (textFieldRef.current) {
        textFieldRef.current.focus();
        const newCursorPos = beforeSlash.length + response.content.length;
        textFieldRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    // Handle canned responses navigation with arrow keys
    if (showCannedResponses && (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'Escape')) {
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowCannedResponses(false);
        setSlashPosition(-1);
        setCannedSearchQuery('');
      }
      // Arrow key navigation could be added here for better UX
      return;
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (value.trim()) {
        onSend();
      }
    }
  };

  const insertFormatting = (format: string) => {
    if (!textFieldRef.current) return;

    const textarea = textFieldRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);

    let formattedText = '';
    switch (format) {
      case 'bold':
        formattedText = selectedText ? `**${selectedText}**` : '**bold text**';
        break;
      case 'italic':
        formattedText = selectedText ? `*${selectedText}*` : '*italic text*';
        break;
      case 'underline':
        formattedText = selectedText ? `__${selectedText}__` : '__underlined text__';
        break;
      case 'code':
        formattedText = selectedText ? `\`${selectedText}\`` : '`code`';
        break;
      case 'link':
        formattedText = selectedText ? `[${selectedText}](url)` : '[link text](url)';
        break;
      case 'bullet':
        formattedText = `\n- ${selectedText || 'list item'}`;
        break;
      case 'number':
        formattedText = `\n1. ${selectedText || 'list item'}`;
        break;
    }

    const newValue = value.substring(0, start) + formattedText + value.substring(end);
    onChange(newValue);
    
    // Focus and set cursor position
    setTimeout(() => {
      if (textarea) {
        textarea.focus();
        const newCursorPos = start + formattedText.length;
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  return (
    <Box sx={{ flexShrink: 0 }}>
      {/* Formatting Toolbar */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 0.3,
        py: 0.5,
        px: 1,
        borderBottom: 1, 
        borderColor: 'divider',
        bgcolor: 'background.paper',
        minHeight: 36   
      }}>
        <Tooltip title="Bold">
          <IconButton size="small" onClick={() => insertFormatting('bold')}>
            <FormatBold fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Italic">
          <IconButton size="small" onClick={() => insertFormatting('italic')}>
            <FormatItalic fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Underline">
          <IconButton size="small" onClick={() => insertFormatting('underline')}>
            <FormatUnderlined fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        
        <Tooltip title="Bullet List">
          <IconButton size="small" onClick={() => insertFormatting('bullet')}>
            <FormatListBulleted fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Numbered List">
          <IconButton size="small" onClick={() => insertFormatting('number')}>
            <FormatListNumbered fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        
        <Tooltip title="Link">
          <IconButton size="small" onClick={() => insertFormatting('link')}>
            <Link fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Code">
          <IconButton size="small" onClick={() => insertFormatting('code')}>
            <Code fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        
        {/* Action Buttons - moved to formatting toolbar */}
        {onAttachFile && (
          <Tooltip title="Attach File">
            <IconButton size="small" onClick={onAttachFile}>
              <AttachFile fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        
        <Tooltip title="Emoji">
          <IconButton
            size="small"
            onClick={(e) => setAnchorEl(e.currentTarget)}
          >
            <EmojiEmotions fontSize="small" />
          </IconButton>
        </Tooltip>

        {onStartRecording && (
          <Tooltip title="Record and send voice message">
            <IconButton size="small" onClick={onStartRecording}>
              <VoiceMessageIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {onStartSpeechToText && (
          <Tooltip title="Convert speech to text">
            <IconButton size="small" onClick={onStartSpeechToText}>
              <SpeechToTextIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {showSignatureButton && onOpenSignatureDialog && (
          <Tooltip title="Signature Settings">
            <IconButton size="small" onClick={onOpenSignatureDialog}>
              <EditNote fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Message Input Area */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, py: 0.5, px: 1 }}>
        <TextField
          inputRef={textFieldRef}
          fullWidth
          multiline
          maxRows={3}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={disabled}
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                border: 'none',
              },
            },
          }}
        />
        
        {/* Send Button */}
        <Button
          variant="contained"
          onClick={onSend}
          disabled={!value.trim() || disabled}
          sx={{ minWidth: 'auto', px: 2, alignSelf: 'flex-end' }}
        >
          <Send fontSize="small" />
        </Button>
      </Box>

      {/* Emoji Picker */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <Paper sx={{ width: 400, maxHeight: 400 }}>
          <Tabs
            value={selectedEmojiCategory}
            onChange={(e, newValue) => setSelectedEmojiCategory(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            {Object.keys(emojiCategories).map((category) => (
              <Tab key={category} label={category} value={category} />
            ))}
          </Tabs>

          <Box sx={{ p: 2, height: 300, overflow: 'auto' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {emojiCategories[selectedEmojiCategory].map((emoji, index) => (
                <IconButton
                  key={index}
                  size="small"
                  onClick={() => handleEmojiClick(emoji)}
                  sx={{
                    fontSize: '1.4em',
                    width: '40px',
                    height: '40px',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  {emoji}
                </IconButton>
              ))}
            </Box>
          </Box>
        </Paper>
      </Popover>

      {/* Canned Responses Dropdown */}
      <Popover
        open={showCannedResponses && cannedResponses.length > 0}
        anchorEl={cannedResponseAnchorEl}
        onClose={() => {
          setShowCannedResponses(false);
          setSlashPosition(-1);
          setCannedSearchQuery('');
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        disableAutoFocus
        disableEnforceFocus
      >
        <Paper sx={{ width: 400, maxHeight: 300, overflow: 'hidden' }}>
          <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', bgcolor: 'primary.main', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <QuestionAnswer fontSize="small" />
              <Typography variant="subtitle2" fontWeight="bold">
                Canned Responses
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {cannedSearchQuery ? `Searching: /${cannedSearchQuery}` : 'Type to search or select from list'}
            </Typography>
          </Box>

          <List sx={{ p: 0, overflow: 'auto', maxHeight: 250 }}>
            {cannedResponses
              .filter(response =>
                !cannedSearchQuery ||
                response.short_code.toLowerCase().includes(cannedSearchQuery.toLowerCase()) ||
                response.content.toLowerCase().includes(cannedSearchQuery.toLowerCase())
              )
              .slice(0, 5)
              .map((response) => (
                <ListItem key={response.id} disablePadding>
                  <ListItemButton
                    onClick={() => handleCannedResponseSelect(response)}
                    sx={{ py: 1.5, px: 2 }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Chip
                            label={`/${response.short_code}`}
                            size="small"
                            color="primary"
                            sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}
                          />
                          {response.is_private && (
                            <Chip
                              label="Private"
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                            Used {response.usage_count} times
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {response.content}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            {cannedResponses.filter(response =>
              !cannedSearchQuery ||
              response.short_code.toLowerCase().includes(cannedSearchQuery.toLowerCase()) ||
              response.content.toLowerCase().includes(cannedSearchQuery.toLowerCase())
            ).length === 0 && (
              <ListItem>
                <ListItemText
                  primary={
                    <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 2 }}>
                      No matching canned responses found
                    </Typography>
                  }
                />
              </ListItem>
            )}
          </List>

          <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
              Press ESC to close • Click to insert
            </Typography>
          </Box>
        </Paper>
      </Popover>
    </Box>
  );
};

export default RichTextEditor;